import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Property, AppUser } from '../../types';
import { getClusterPropertiesApi } from '../../services/propertyApiClient';
import { useMapPins } from '../../components/broker/map/useMapPins';
import { MapControlButtons } from '../../components/broker/map/MapControlButtons';
import { MapSidebarCards } from '../../components/broker/map/MapSidebarCards';
import { createGeoJsonLayer } from '../../components/broker/map/mapLeafletHelper';
import { useMapDrawingInteraction } from '../../components/broker/map/useMapDrawingInteraction';
import { useMapClusterLayer } from '../../components/broker/map/useMapClusterLayer';
import { useMapInstance } from '../../components/broker/map/useMapInstance';

interface MapResultsViewProps {
  paginatedProperties: (Property & { sourceCollection?: any })[];
  allFilteredProperties: (Property & { sourceCollection?: any })[];
  isLoading: boolean;
  onViewProperty: (property: Property) => void;
  onShareProperty: (property: Property) => void;
  spatialFilter: any;
  onSpatialFilterChange: (filter: any) => void;
  currentSelectedPropertiesForCsv: string[];
  onToggleSelectPropertyForCsv: (id: string) => void;
  appUser: AppUser | null;
  onInterested: (property: Property) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  inmobiliariasConfig: Record<string, any>;
  filterInput?: any;
}

export const MapResultsView: React.FC<MapResultsViewProps> = (props) => {
  const {
    paginatedProperties, allFilteredProperties, isLoading, onViewProperty,
    onShareProperty, spatialFilter, onSpatialFilterChange, currentSelectedPropertiesForCsv,
    onToggleSelectPropertyForCsv, appUser, onInterested, currentPage,
    totalPages, onPageChange, inmobiliariasConfig, filterInput,
  } = props;

  const [interactionMode, setInteractionMode] = useState<'none' | 'draw' | 'select'>('none');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [clusterIds, setClusterIds] = useState<string[]>([]);
  const [clusterProperties, setClusterProperties] = useState<Property[] | null>(null);
  const [clusterPage, setClusterPage] = useState(1);
  const [clusterTotalPages, setClusterTotalPages] = useState(1);
  const [clusterTotal, setClusterTotal] = useState(0);
  const [isLoadingCluster, setIsLoadingCluster] = useState(false);

  const activeFilterLayerRef = useRef<any>(null);
  const handleInteraction = useCallback(() => setHasInteracted(true), []);
  const { mapDivRef, mapInstance, markersClusterRef } = useMapInstance(handleInteraction);
  const { pins, isLoadingPins } = useMapPins({ ...(filterInput || {}), spatialFilter, appUser });

  const loadClusterPage = useCallback(async (ids: string[], page: number) => {
    setIsLoadingCluster(true);
    try {
      const res = await getClusterPropertiesApi(ids, page, 5);
      setClusterProperties(res.items);
      setClusterPage(res.page);
      setClusterTotalPages(res.totalPages);
      setClusterTotal(res.total);
    } catch (e) {
      console.error('[MapResultsView] Cluster loading error:', e);
    } finally {
      setIsLoadingCluster(false);
    }
  }, []);

  const handleSelectProperty = useCallback((p: Property) => {
    setSelectedProperty(p);
    setClusterProperties(null);
  }, []);

  const handleSelectClusterIds = useCallback((ids: string[]) => {
    setClusterIds(ids);
    setSelectedProperty(null);
    loadClusterPage(ids, 1);
  }, [loadClusterPage]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    if (activeFilterLayerRef.current) {
      map.removeLayer(activeFilterLayerRef.current);
      activeFilterLayerRef.current = null;
    }
    if (spatialFilter) {
      const layer = createGeoJsonLayer(spatialFilter);
      if (layer) {
        layer.addTo(map);
        activeFilterLayerRef.current = layer;
        try {
          map.fitBounds(layer.getBounds(), { padding: [40, 40], maxZoom: 16 });
        } catch {
          if (spatialFilter.center) map.setView([spatialFilter.center.lat, spatialFilter.center.lon || spatialFilter.center.lng], 16);
        }
      }
    }
  }, [spatialFilter, mapInstance]);

  useMapClusterLayer({
    mapInstance, markersClusterRef, pins, allFilteredProperties,
    onSelectProperty: handleSelectProperty, onSelectClusterIds: handleSelectClusterIds,
  });

  useMapDrawingInteraction({ mapInstance, interactionMode, setInteractionMode, onSpatialFilterChange });

  return (
    <div className="flex flex-col h-full animate-fade-in">
      <div className="flex flex-col md:flex-row flex-grow min-h-[500px] h-[calc(100vh-250px)] relative overflow-hidden rounded-xl border border-gray-100 shadow-inner">
        <div className="relative h-[400px] md:h-full w-full md:w-2/3 lg:w-3/4 z-0 bg-gray-50 flex flex-col">
          <div className="h-full w-full" ref={mapDivRef}></div>
          <MapControlButtons
            interactionMode={interactionMode}
            hasInteracted={hasInteracted}
            hasSpatialFilter={Boolean(spatialFilter)}
            onToggleMode={(m) => { setHasInteracted(true); setInteractionMode((prev) => (prev === m ? 'none' : m)); }}
            onClearSpatialFilter={() => { setHasInteracted(true); onSpatialFilterChange(null); }}
          />
        </div>

        <MapSidebarCards
          totalCount={pins.length || allFilteredProperties.length}
          hasSpatialFilter={Boolean(spatialFilter)}
          selectedProperty={selectedProperty}
          onClearSelectedProperty={() => setSelectedProperty(null)}
          clusterProperties={clusterProperties}
          isLoadingCluster={isLoadingCluster}
          clusterTotal={clusterTotal}
          clusterPage={clusterPage}
          clusterTotalPages={clusterTotalPages}
          onClusterPageChange={(p) => loadClusterPage(clusterIds, p)}
          onClearCluster={() => setClusterProperties(null)}
          defaultProperties={paginatedProperties}
          isLoadingDefault={isLoading || isLoadingPins}
          currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange}
          currentSelectedPropertiesForCsv={currentSelectedPropertiesForCsv}
          onToggleSelectPropertyForCsv={onToggleSelectPropertyForCsv}
          onViewProperty={onViewProperty} onShareProperty={onShareProperty}
          appUser={appUser} onInterested={onInterested}
          inmobiliariasConfig={inmobiliariasConfig}
        />
      </div>
    </div>
  );
};
